import { useCallback, useMemo, useRef, useState } from 'react'

import { documentsApi } from '@/lib/api'
import type { ReviewReceipt } from '@/lib/api/documents'
import {
  isResultFieldReviewed,
  pageByNumber,
  resultFieldFlagReason,
  resultFieldValue,
} from '@/types/domain'
import type {
  BoxConfiguration,
  DocumentResultField,
  EvaluationDocument,
  Position,
  ResultFieldFlag,
  Template,
  TextType,
} from '@/types/domain'

import type { ReviewFieldType } from './fieldValue'
import {
  appliedField,
  optimisticField,
  reviewSubmission,
  type Reviewer,
  type ReviewSubmission,
} from './reviewWrite'

/**
 * The ordered list of fields a person is working through, the advance, and
 * the write.
 *
 * **One queue, two orderings.** The per-document screen walks the fields of
 * one document in page order; the bulk screen walks one kind of problem
 * across many documents, ordered by confidence. That is the only difference:
 * an item carries its own `documentId`, so nothing else in here has to know
 * which screen it is serving. The caller builds and orders the array, this
 * hook owns the cursor and the writes. (Where it does *not* hold: the bulk
 * screen pages in more documents as it goes, so its array grows underneath
 * the cursor — which is why the cursor is a key and not an index, and why
 * local writes are kept in an override map rather than in a copy of the
 * list.)
 */

export interface ReviewQueueItem {
  /** `documentId:fieldId` — unique across documents, stable while paging. */
  key: string
  documentId: string
  /** The scan's file name, for the bulk screen's second line. */
  documentTitle?: string
  /** The box id, which is how `EvaluationDocument.result` is keyed. */
  fieldId: string
  label: string
  type: ReviewFieldType
  textType?: TextType
  /** The stored analysis. The delivered value is never read off this
   *  directly — see `value`. */
  field: DocumentResultField
  /** The delivered value, through `resultFieldValue`: the reviewed text once
   *  somebody reviewed it, **including when that text is empty**. */
  value: string
  confidence: number
  reviewed: boolean
  /** Why it is waiting, in the only two terms the data supports, or null. */
  flag: ResultFieldFlag | null
  /** The field's rectangle in the page image's own pixels, or null. */
  rect: Position | null
}

const ITEM_TYPE: Record<string, ReviewFieldType> = {
  textDetection: 'text',
  checkBox: 'checkbox',
  // A radio group is a row of checkboxes to the person reading it.
  radioBox: 'checkbox',
  signature: 'signature',
}

function rectOf(field: DocumentResultField): Position | null {
  const width = field.x2 - field.x
  const height = field.y2 - field.y
  if (!(width > 0) || !(height > 0)) return null
  return { x: field.x, y: field.y, width, height }
}

/** Builds one item, deriving everything the screens show from the field and
 *  its box. Kept in one place so an optimistic write can re-derive exactly
 *  what the initial build produced. */
export function reviewQueueItem(
  documentId: string,
  fieldId: string,
  field: DocumentResultField,
  box: BoxConfiguration | undefined,
  threshold: number,
  documentTitle?: string,
): ReviewQueueItem {
  const flag = resultFieldFlagReason(field, threshold)
  return {
    key: `${documentId}:${fieldId}`,
    documentId,
    documentTitle,
    fieldId,
    // The result map is keyed by box id, so the name only exists on the
    // template. A box deleted after the analysis leaves its id, which is
    // unlovely but true; inventing a name would be worse.
    label: box?.label ?? fieldId,
    type: ITEM_TYPE[box?.type ?? 'textDetection'] ?? 'text',
    textType: box?.textType,
    field,
    value: resultFieldValue(field),
    confidence: field.confidence ?? 0,
    reviewed: isResultFieldReviewed(field),
    flag,
    rect: rectOf(field),
  }
}

export interface QueueBuildOptions {
  /** Only the fields waiting for a person — the bulk screen's whole list,
   *  and the per-document screen's "Unklar" filter. A field the analyzer
   *  flagged counts even when the two derivable reasons do not fire: it
   *  knows things confidence does not, such as ink in a field that read as
   *  empty. */
  flaggedOnly?: boolean
  documentTitle?: (doc: EvaluationDocument) => string | undefined
}

/**
 * Joins documents' stored analyses against the layouts they matched.
 *
 * Order follows the template's boxes within a document, so the fields run
 * down the page the way the form does. Results whose box is gone are
 * appended rather than dropped: they are data somebody paid for.
 */
export function reviewQueueItems(
  documents: EvaluationDocument[],
  template: Template | undefined,
  threshold: number,
  options: QueueBuildOptions = {},
): ReviewQueueItem[] {
  const items: ReviewQueueItem[] = []
  for (const doc of documents) {
    const result = doc.result ?? {}
    const page =
      template && doc.matchedPage != null ? pageByNumber(template, doc.matchedPage) : undefined
    const boxes = page?.boxConfiguration ?? []
    const title = options.documentTitle?.(doc) ?? doc.title
    const flagged = new Set(doc.needsReviewFields ?? [])

    const seen = new Set<string>()
    const push = (id: string, box: BoxConfiguration | undefined) => {
      const field = result[id]
      if (!field) return
      const item = reviewQueueItem(doc.id, id, field, box, threshold, title)
      if (options.flaggedOnly) {
        const waiting = !item.reviewed && (item.flag != null || flagged.has(id) || field.needsReview === true)
        if (!waiting) return
      }
      items.push(item)
    }

    for (const box of boxes) {
      seen.add(box.id)
      push(box.id, box)
    }
    for (const id of Object.keys(result)) {
      if (seen.has(id)) continue
      push(id, undefined)
    }
  }
  return items
}

/** The same item with a newer field: what an optimistic write, and then the
 *  server's receipt, produce. Everything derived is derived again, so a
 *  reviewed field stops being flagged and its value follows the precedence
 *  rule rather than staying at the recognizer's text. */
function rederive(
  item: ReviewQueueItem,
  field: DocumentResultField,
  threshold: number,
): ReviewQueueItem {
  return {
    ...item,
    field,
    value: resultFieldValue(field),
    confidence: field.confidence ?? 0,
    reviewed: isResultFieldReviewed(field),
    flag: resultFieldFlagReason(field, threshold),
    rect: rectOf(field),
  }
}

/** The write, injectable so tests and a future batch endpoint do not need a
 *  running gateway. */
export type SubmitReview = (args: {
  templateId: string
  documentId: string
  fieldId: string
  accepted: boolean
  correction?: string
}) => Promise<ReviewReceipt>

const defaultSubmit: SubmitReview = ({ templateId, documentId, fieldId, accepted, correction }) =>
  documentsApi.addReview(templateId, documentId, { fieldId, accepted, correction })

export interface UseReviewQueueOptions {
  templateId: string
  /** The ordered queue. The caller owns the ordering and may grow it. */
  items: ReviewQueueItem[]
  /** The template's `confidenceThreshold`; decides what counts as flagged. */
  threshold?: number
  reviewer?: Reviewer
  submit?: SubmitReview
  /** Called after a write lands, so the screen can fold the new field into
   *  whatever cache it loaded the document from. */
  onWritten?: (item: ReviewQueueItem, field: DocumentResultField, receipt: ReviewReceipt) => void
  onError?: (item: ReviewQueueItem, error: unknown) => void
}

export interface ReviewWriteOutcome {
  submission: ReviewSubmission
  /** False when the write failed and was rolled back, or when there was no
   *  such item to write. */
  ok: boolean
  error?: unknown
}

export function useReviewQueue({
  templateId,
  items: source,
  threshold = 0.8,
  reviewer,
  submit = defaultSubmit,
  onWritten,
  onError,
}: UseReviewQueueOptions) {
  // Local writes live beside the caller's array rather than in a copy of it.
  // The bulk screen appends a page of documents while a write is in flight;
  // a copied list would either lose the new page or lose the write.
  const [overrides, setOverrides] = useState<Record<string, DocumentResultField>>({})
  const [pending, setPending] = useState<string[]>([])
  const [error, setError] = useState<{ item: ReviewQueueItem; error: unknown } | null>(null)
  const [activeKey, setActiveKey] = useState<string | null>(null)

  const items = useMemo(
    () => source.map((item) => (overrides[item.key] ? rederive(item, overrides[item.key], threshold) : item)),
    [source, overrides, threshold],
  )

  /**
   * The same local writes folded onto any other list of the same items.
   *
   * The bulk screen filters by field type before handing the queue its
   * source, but its tab counts and its progress bar have to speak for
   * *everything* loaded — counted off `items` they would read (0) for every
   * type but the one on screen, which is a claim that the work is done.
   */
  const applyOverrides = useCallback(
    (list: ReviewQueueItem[]) =>
      list.map((item) => (overrides[item.key] ? rederive(item, overrides[item.key], threshold) : item)),
    [overrides, threshold],
  )

  const open = useMemo(() => items.filter((item) => !item.reviewed), [items])
  const resolvedCount = items.length - open.length

  const byKey = useMemo(() => new Map(items.map((item) => [item.key, item])), [items])
  const active = activeKey ? byKey.get(activeKey) : undefined
  // Nothing focused yet, or the focused item is gone (a page reloaded, a
  // document was moved): fall to the first thing still waiting.
  const effective = active ?? open[0] ?? items[0]
  const effectiveKey = effective?.key ?? null
  const activeIndex = effective ? items.findIndex((item) => item.key === effective.key) : -1

  const latest = useRef({ items, effectiveKey })
  latest.current = { items, effectiveKey }

  const focus = useCallback((key: string | null) => setActiveKey(key), [])

  const step = useCallback((delta: number) => {
    const { items: list, effectiveKey: current } = latest.current
    if (list.length === 0) return
    const index = list.findIndex((item) => item.key === current)
    const next = Math.min(list.length - 1, Math.max(0, (index < 0 ? 0 : index) + delta))
    setActiveKey(list[next].key)
  }, [])

  const next = useCallback(() => step(1), [step])
  const previous = useCallback(() => step(-1), [step])

  /** After a review: the next field still waiting, wrapping to the first one
   *  so the last correction on a page does not dead-end the screen. Falls
   *  back to the next item in order when nothing is open — the person is
   *  done, and the cursor should not jump to the top. */
  const advance = useCallback((resolvedKey: string) => {
    const { items: list } = latest.current
    const index = list.findIndex((item) => item.key === resolvedKey)
    // The list this reads is the one from the last render, where the field
    // just written still looks open — React has not re-rendered between the
    // write and this call. Hence the explicit exclusion of its own key.
    const after = list.slice(index + 1).find((item) => !item.reviewed && item.key !== resolvedKey)
    const wrapped = after ?? list.find((item) => !item.reviewed && item.key !== resolvedKey)
    if (wrapped) {
      setActiveKey(wrapped.key)
      return
    }
    if (index >= 0 && index + 1 < list.length) setActiveKey(list[index + 1].key)
  }, [])

  const write = useCallback(
    async (item: ReviewQueueItem, submission: ReviewSubmission): Promise<ReviewWriteOutcome> => {
      const before = item.field
      const optimistic = optimisticField(before, submission, reviewer)
      setOverrides((current) => ({ ...current, [item.key]: optimistic }))
      setPending((current) => [...current, item.key])
      setError(null)
      try {
        const receipt = await submit({
          templateId,
          documentId: item.documentId,
          fieldId: item.fieldId,
          accepted: submission.kind === 'accept',
          correction: submission.kind === 'correct' ? submission.correction : undefined,
        })
        const settled = appliedField(before, optimistic, receipt)
        setOverrides((current) => ({ ...current, [item.key]: settled }))
        onWritten?.(item, settled, receipt)
        advance(item.key)
        return { submission, ok: true }
      } catch (cause) {
        // Rollback. A correction that looks saved and is not is the worst
        // failure this screen has: the person moves on believing the value
        // is right, and nothing ever tells them otherwise.
        setOverrides((current) => {
          const rest = { ...current }
          delete rest[item.key]
          return rest
        })
        setError({ item, error: cause })
        onError?.(item, cause)
        return { submission, ok: false, error: cause }
      } finally {
        setPending((current) => current.filter((key) => key !== item.key))
      }
    },
    [advance, onError, onWritten, reviewer, submit, templateId],
  )

  /** Submits what is in the editor. Unchanged is an acceptance — the fast
   *  path, and the common one. */
  const submitValue = useCallback(
    (value: string, key?: string): Promise<ReviewWriteOutcome> => {
      const item = byKey.get(key ?? effectiveKey ?? '')
      if (!item) return Promise.resolve({ submission: { kind: 'accept' as const }, ok: false })
      return write(item, reviewSubmission(item.value, value))
    },
    [byKey, effectiveKey, write],
  )

  /** Signs off on what was read, without going through an editor. */
  const accept = useCallback(
    (key?: string): Promise<ReviewWriteOutcome> => {
      const item = byKey.get(key ?? effectiveKey ?? '')
      if (!item) return Promise.resolve({ submission: { kind: 'accept' as const }, ok: false })
      return write(item, { kind: 'accept' })
    },
    [byKey, effectiveKey, write],
  )

  return {
    items,
    applyOverrides,
    open,
    total: items.length,
    resolvedCount,
    active: effective,
    activeKey: effectiveKey,
    activeIndex,
    focus,
    next,
    previous,
    accept,
    submitValue,
    /** Keys with a write in flight, so a row can show it and a second Enter
     *  cannot double-post. */
    pending,
    isPending: (key: string) => pending.includes(key),
    error,
    clearError: () => setError(null),
  }
}
